'use client';

import React, { memo } from 'react';

import BalancedContentSection from '@/components/sections/BalancedContentSection/BalancedContentSection';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';

import type { InsuranceLogo, InsuranceSectionData } from './types';

const LogoRow = ({
  logos,
  offsetClass = '',
}: {
  logos: InsuranceLogo[];
  offsetClass?: string;
}) => (
  <div
    className={`desktop:justify-start desktop:gap-8 flex justify-center gap-3 ${offsetClass}`}
  >
    {logos.map((logo, index) => {
      let customImageClass =
        'object-contain filter brightness-100 hover:brightness-75 transition-all duration-300 rounded-full';
      let containerClass =
        'bg-white rounded-full shadow-sm hover:shadow-md transition-shadow duration-300 p-2 laptop:p-4 desktop:p-6 flex shrink-0 size-[65px] laptop:size-[85px] desktop:size-[100px] items-center justify-center overflow-hidden';

      const titleLower = logo.title?.toLowerCase() || '';

      if (titleLower.includes('axa')) {
        customImageClass =
          'object-cover object-left filter brightness-100 hover:brightness-75 transition-all duration-300 rounded-full scale-[1.3]';
      } else if (titleLower.includes('wpa')) {
        customImageClass =
          'object-contain transition-all duration-300 rounded-full opacity-100';
        containerClass =
          'bg-white rounded-full shadow-sm hover:shadow-md transition-shadow duration-300 p-2 laptop:p-3 desktop:p-4 flex shrink-0 size-[65px] laptop:size-[85px] desktop:size-[100px] items-center justify-center overflow-hidden';
      } else if (titleLower.includes('uhg')) {
        containerClass =
          'bg-white rounded-full shadow-sm hover:shadow-md transition-shadow duration-300 p-1 flex shrink-0 size-[65px] laptop:size-[85px] desktop:size-[100px] items-center justify-center overflow-hidden';
        customImageClass =
          'object-contain transition-all duration-300 rounded-full scale-[1.3]';
      }

      return (
        <div
          key={`${logo.title || logo.url || 'insurance-logo'}-${index}`}
          className={containerClass}
        >
          <R2Image
            path={logo.url || ''}
            alt={logo.title || 'Insurance logo'}
            width={60}
            height={60}
            // Logos render at a fixed 52px (mobile) to ~76px (desktop, inside
            // the 100px padded container) — without this, R2Image's default
            // sizes ("100vw" below 640px) requested a far larger srcset
            // candidate than this small, fixed-size logo ever needs.
            sizes="100px"
            className={customImageClass}
          />
        </div>
      );
    })}
  </div>
);

const InsuranceSection: React.FC<{
  data?: InsuranceSectionData;
}> = ({ data }) => {
  if (!data) return null;

  const insuranceLogos = data?.imagesCollection?.items || [];

  const LogoGrid = (
    <div className="desktop:space-y-8 w-full space-y-4">
      <LogoRow logos={insuranceLogos.slice(0, 4)} offsetClass="lg:ml-12" />
      <LogoRow logos={insuranceLogos.slice(4, 8)} offsetClass="ml-0" />
      <LogoRow logos={insuranceLogos.slice(8, 12)} offsetClass="lg:ml-12" />
    </div>
  );

  const TextContent = (
    <div className="desktop:space-y-8 flex min-h-[300px] flex-col justify-center space-y-6">
      {data.heading && (
        <RichText
          styles={{
            p: 'rich-text-heading font-bold text-theme-brand-blue leading-tight min-w-full lg:min-w-[650px] wrap-break-word block min-[1024px]:max-[1279px]:min-w-0! min-[1024px]:max-[1279px]:text-5xl! min-[1024px]:max-[1279px]:leading-tight!',
          }}
        >
          {data.heading}
        </RichText>
      )}
      {data.description && (
        <RichText
          styles={{
            className: 'rich-text-body text-gray-600 leading-relaxed max-w-lg',
          }}
        >
          {data.description}
        </RichText>
      )}
    </div>
  );

  return (
    <BalancedContentSection
      mediaContent={LogoGrid}
      textContent={TextContent}
      textRatioClassName="lg:w-[55%] desktop:w-1/2"
      gapClassName="gap-8 lg:gap-6 desktop:gap-12"
      data={{
        backgroundColor: 'bg-surface-light',
        reverseOnMobile: true,
        textPosition: 'left',
        imageRatio: 'lg:w-[45%] desktop:w-1/2',
      }}
    />
  );
};

export default memo(InsuranceSection);
